import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { OrdersListRefreshService } from 'src/app/services/offline/orders-list-refresh.service';
import { TransportistaViajesFirmaGatewayApi } from 'src/core/apis/viajes.firma';
import { TransportistaSessionStorage } from 'src/core/jwt/session.storage';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { EVENT_LOG_ENDPOINTS } from 'src/core/logging/event-log-endpoints';
import {
  httpErrorContext,
  httpSuccessContext,
  mergeHttpLogPayload,
} from 'src/core/logging/http-event-log-context';
import {
  assertViajesFirmaPayload,
  buildViajesFirmaRequest,
  isViajesFirmaSuccessResponse,
  parseViajesFirmaErrorMessage,
} from './viajes-firma.mapper';
import {
  ViajesFirmaRejectedError,
  isTerminalFirmaSendError,
  mapViajesFirmaAlreadySignedError,
  mapViajesFirmaSubmitError,
} from './viajes-firma-errors';
import { ViajesFirmaStateService } from './viajes-firma-state.service';
import { ViajesFirmaTipo } from 'src/core/apis/viajes.firma';

export type ViajesFirmaSubmitResult =
  | { status: 'sent' }
  | { status: 'queued' };

@Injectable({ providedIn: 'root' })
export class ViajesFirmaService {
  constructor(
    private readonly offlineStorage: OfflineStorageService,
    private readonly viajesFirmaGateway: TransportistaViajesFirmaGatewayApi,
    private readonly gatewaySession: TransportistaSessionStorage,
    private readonly ordersListRefresh: OrdersListRefreshService,
    private readonly eventLog: EventLogService,
    private readonly firmaState: ViajesFirmaStateService
  ) {}

  hasGatewayToken(): boolean {
    return !!this.gatewaySession.getToken();
  }

  setSignedGuideCanvas(
    tkt: string,
    orderNumber: string,
    rut: string,
    name: string,
    signature: string,
    email: string,
    isAlreadySigned = false,
    tipoFirma?: ViajesFirmaTipo,
    comentario?: string,
    emailDos?: string
  ): Observable<ViajesFirmaSubmitResult> {
    try {
      assertViajesFirmaPayload({
        tkt,
        orderNumber,
        rut,
        name,
        signature,
        email,
        emailDos,
        tipoFirma,
        comentario,
      });
    } catch (e) {
      this.eventLog.log(
        'firma-digital',
        'error',
        mergeHttpLogPayload(
          { action: 'validate' },
          {
            endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
            codigoError: 'VALIDATION_ERROR',
            message: (e as Error).message,
          }
        )
      );
      // Este proyecto usa RxJS 6.6.7: throwError recibe directamente el error.
      return throwError(e);
    }

    const isRecepcion = !tipoFirma || tipoFirma === 'RECEPCION';
    if (isAlreadySigned && isRecepcion) {
      const err = mapViajesFirmaAlreadySignedError();
      return throwError(new Error(err.message));
    }

    this.firmaState.beginSubmit(tkt);

    return this.sendSignedGuideCanvas(
      tkt,
      orderNumber,
      rut,
      name,
      signature,
      email,
      tipoFirma,
      comentario,
      emailDos
    ).pipe(
      map(() => ({ status: 'sent' } as ViajesFirmaSubmitResult)),
      catchError((err) => {
        if (isTerminalFirmaSendError(err)) {
          this.firmaState.markError(tkt, mapViajesFirmaSubmitError(err));
          return throwError(err);
        }
        this.eventLog.log('firma-digital', 'queue_canvas', { tkt, orderNumber, tipoFirma });
        return from(
          this.offlineStorage
            .addOperationToQueue(tkt, {
              type: 'signedCanvas',
              payload: {
                tkt,
                orderNumber,
                rut,
                name,
                signature,
                email,
                emailDos,
                tipoFirma,
                comentario,
              },
            })
            .then(() => ({ status: 'queued' } as ViajesFirmaSubmitResult))
        );
      })
    );
  }

  sendSignedGuideCanvas(
    tkt: string,
    orderNumber: string,
    rut: string,
    name: string,
    signature: string,
    email: string,
    tipoFirma?: ViajesFirmaTipo,
    comentario?: string,
    emailDos?: string
  ): Observable<unknown> {
    if (!this.hasGatewayToken()) {
      return throwError(
        new Error('Sesión no válida: falta token Bearer para /viajes/firma.')
      );
    }

    const body = buildViajesFirmaRequest({
      tkt,
      orderNumber,
      rut,
      name,
      signature,
      email,
      emailDos,
      tipoFirma,
      comentario,
    });

    this.eventLog.log('firma-digital', 'send_canvas', { tkt, orderNumber });

    return this.viajesFirmaGateway
      .post(body, {
        headers: new HttpHeaders()
          .set('X-Background-Request', '1')
          .set('X-Skip-Loader', '1'),
      })
      .pipe(
        mergeMap((raw) => {
          if (!isViajesFirmaSuccessResponse(raw)) {
            return throwError(
              new ViajesFirmaRejectedError(parseViajesFirmaErrorMessage(raw))
            );
          }
          return of(raw);
        }),
        tap({
          next: () => {
            this.ordersListRefresh.requestListRefresh();
            this.firmaState.markConfirmed(tkt);
            this.eventLog.log(
              'firma-digital',
              'send_success',
              mergeHttpLogPayload(
                { tkt, orderNumber },
                httpSuccessContext(EVENT_LOG_ENDPOINTS.VIAJES_FIRMA)
              )
            );
          },
          error: (err) => {
            const httpCtx =
              err instanceof HttpErrorResponse
                ? httpErrorContext(err, EVENT_LOG_ENDPOINTS.VIAJES_FIRMA)
                : {
                    endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
                    message: err instanceof Error ? err.message : String(err),
                  };
            this.eventLog.log(
              'firma-digital',
              'error',
              mergeHttpLogPayload(
                { action: 'send_canvas', tkt, orderNumber },
                httpCtx
              )
            );
          },
        })
      );
  }

}
